import dayjs from "dayjs";
import BaseInvoiceStatus from "../../../components/ui/BaseInvoiceStatus";
import BaseWrapper from "../../../components/ui/BaseWrapper";
import { UserType } from "../../../utils/enum";
import UserProfileLink from "../../../components/ui/UserProfileLink";
import useAuth from "../../../hooks/useAuth";
import { formatPhoneNumber } from "../../../utils/helpers";

const EditInvoiceListingHeader = ({ invoice }) => {
  const { authRole } = useAuth();
  const statusChangedBy = invoice?.statusHistory[invoice?.statusHistory?.length - 1];

  return (
    <>
      <div className="border-b border-zinc-300 bg-white">
        <BaseWrapper>
          <div className="py-[7px] flex items-center  justify-between">
            <div>
              <BaseInvoiceStatus
                status={invoice?.status}
                statusHistory={invoice?.statusHistory}
                isCustomer={authRole === UserType.CUSTOMER}
              />
              <h3 className="font-light">INV-#{invoice?.id}</h3>
            </div>
            {invoice?.role === UserType.CLIENT && (
              <div className="flex items-center">
                <div className="shadow-md rounded-md py-3 px-4  mr-4">
                  <p className="!text-primary text-base font-medium mb-2">Billing From</p>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Name</p>
                    <p>{invoice?.branch?.name}</p>
                  </div>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Address</p>
                    <p>{invoice?.branch?.address?.addressLine1}</p>
                  </div>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Email</p>
                    <p>{invoice?.branch?.email}</p>
                  </div>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Phone</p>

                    <p className="table-font-size cursor-default">
                      {formatPhoneNumber(invoice?.branch?.phone)}
                    </p>
                  </div>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">{invoice?.branch?.taxType}</p>
                    <p>{invoice?.branch?.taxNumber}</p>
                  </div>
                </div>
                <div className="shadow-md rounded-md py-3 px-4  mr-4">
                  <p className="!text-primary text-base font-medium mb-2">Billing To</p>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Name</p>
                    <p>{invoice?.client?.clientName}</p>
                  </div>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Address</p>
                    <p>{invoice?.client?.address?.addressLine1}</p>
                  </div>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Email</p>
                    <p>{invoice?.client?.email}</p>
                  </div>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Phone</p>

                    <p className="table-font-size cursor-default">
                      {formatPhoneNumber(invoice?.client?.phoneNumber)}
                    </p>
                  </div>
                </div>
                <div className="shadow-md rounded-md py-3 px-4 h-[150px]">
                  <p className="!text-primary text-base font-medium mb-2">Invoice Details</p>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Invoice Date</p>
                    <p>{dayjs(invoice?.createdAt).format("DD MMM, YYYY")}</p>
                  </div>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Due Date</p>
                    <p>{dayjs(invoice?.dueDate).format("DD MMM, YYYY")}</p>
                  </div>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Payment Terms</p>
                    <p>{invoice?.paymentTerms} Days</p>
                  </div>
                </div>
              </div>
            )}
            {invoice?.role === UserType.FREELANCER && (
              <div className="flex items-center">
                <div className="shadow-md rounded-md py-3 px-4  mr-4">
                  <p className="!text-primary text-base font-medium mb-2">Billing From</p>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Name</p>
                    {/* A vendor owner bills as their company, so the name is the
                        company's; the link still opens the owner's profile. */}
                    <UserProfileLink userId={invoice?.engineer?.id} className="capitalize">
                      {invoice?.billingFrom?.name}
                    </UserProfileLink>
                  </div>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Address</p>
                    <p>{invoice?.billingFrom?.address?.addressLine1}</p>
                  </div>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Email</p>
                    <p>{invoice?.billingFrom?.email}</p>
                  </div>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Phone</p>

                    <p className="table-font-size cursor-default">
                      {formatPhoneNumber(invoice?.billingFrom?.phone)}
                    </p>
                  </div>
                  {invoice?.billingFrom?.abn && (
                    <div className="flex items-center mb-1">
                      <p className="min-w-36 text-dark font-normal">ABN</p>
                      <p className="capitalize">{invoice?.billingFrom?.abn}</p>
                    </div>
                  )}
                </div>
                <div className="shadow-md rounded-md py-3 px-4  mr-4">
                  <p className="!text-primary text-base font-medium mb-2">Billing To</p>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Name</p>
                    <p>{invoice?.branch?.name}</p>
                  </div>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Address</p>
                    <p>{invoice?.branch?.address?.addressLine1}</p>
                  </div>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Email</p>
                    <p>{invoice?.branch?.email}</p>
                  </div>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Phone</p>

                    <p className="table-font-size cursor-default">
                      {formatPhoneNumber(invoice?.branch?.phone)}
                    </p>
                  </div>
                </div>

                <div className="shadow-md rounded-md py-3 px-4 h-[150px]">
                  <p className="!text-primary text-base font-medium mb-2">Invoice Details</p>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Invoice Date</p>
                    <p>{dayjs(invoice?.createdAt).format("DD MMM, YYYY")}</p>
                  </div>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Due Date</p>
                    <p>{dayjs(invoice?.dueDate).format("DD MMM, YYYY")}</p>
                  </div>
                  <div className="flex items-center mb-1">
                    <p className="min-w-36 text-dark font-normal">Payment Terms</p>
                    <p>{invoice?.paymentTerms} Days</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </BaseWrapper>
      </div>
    </>
  );
};

export default EditInvoiceListingHeader;
